import { UI_FOLDER } from "./../utils/consts";
import { OrderLevelDiscount, ProductLevelDiscount } from "./../entity/discount.types";
import { fetchJSON } from "lib/fetch";
import { BasePromotionRequest, PriceEntity } from "../entity/price-entity/promotions.types";
import {
    BaseRequestOptions,
    PriceActionOptions,
    PriceActionType,
    PriceConditionOptions,
} from "../entity/promotions-dtos.types";
import { PriceCondition } from "../entity/price-entity/price-condition.types";
import { Action, MessageVariable } from "../entity/price-entity/price-action.types";
import isEmpty from "lodash/isEmpty";
import moment from "moment";
import { generateAmountCondition } from "../utils/amount-condition";
import { PriceCode } from "../entity/price-code";
import { getCurrencyInfo } from "lib/globalize-util";

export enum PromotionScope {
    PRICE_ACTION_SCOPE_TOTAL = "PRICE_ACTION_SCOPE_TOTAL",
    PRICE_ACTION_SCOPE_LINE_ITEM = "PRICE_ACTION_SCOPE_LINE_ITEM",
}

export enum PromotionType {
    PRICE_SECTION_TYPE_PROMOTION_APPLIED = "PRICE_SECTION_TYPE_PROMOTION_APPLIED",
    PRICE_SECTION_TYPE_PROMOTION_THRESHOLD = "PRICE_SECTION_TYPE_PROMOTION_THRESHOLD",
}

export enum PriceType {
    Pricing = "ENTITY_TYPE_PRICING",
}

interface Fields {
    scope: PromotionScope;
    type: PromotionType;
    actionType: string;
}

export enum MessageVariableType {
    NUMERIC_FORMAT_MONEY = "NUMERIC_FORMAT_MONEY",
    NUMERIC_FORMAT_INTEGER = "NUMERIC_FORMAT_INTEGER",
}

export enum MessageVariableName {
    QTY = "QTY",
    AMOUNT = "AMOUNT",
}

export default class PromotionUtility {
    /**
     * Create a promotion,
     * `endpoint` will be appserver if only conditional date range rule exist.
     * Otherwise it will point to price service.
     * For hitting the appserver noDomain should be `false` and for price-srv it should be `true`.
     *
     * @param merchantId merchant id
     * @param data price entity
     * @param conditionalEndpoint flag to decide endpoint and noDomain config
     * @returns created entity
     */
    async callCreatePromotionPoint(
        merchantId: string,
        data: string,
        conditionalEndpoint: boolean,
    ): Promise<PriceEntity> {
        const priceServiceEndpoint = `/api/v1/merchant/${merchantId}/pricing/entities`;
        const appserverEndpoint = "/api/v1/promotions";
        const endpoint = conditionalEndpoint ? appserverEndpoint : priceServiceEndpoint;
        return fetchJSON<PriceEntity>(endpoint, {
            method: "POST",
            noDomain: !conditionalEndpoint,
            body: data,
        });
    }

    /**
     * Update a promotion.
     * `endpoint` will be appserver if only conditional date range rule exist.
     * Otherwise it will point to price service.
     * For hitting the appserver noDomain should be `false` and for price-srv it should be `true`.
     *
     * @param merchantId merchant id
     * @param data price entity
     * @param entityId price entity id
     * @param conditionalEndpoint flag to decide endpoint and noDomain config
     * @returns updated entity
     */
    async callUpdatePromotionPoint(
        merchantId: string,
        data: string,
        entityId: string,
        conditionalEndpoint: boolean,
    ): Promise<PriceEntity> {
        const priceServiceEndpoint = `/api/v1/merchant/${merchantId}/pricing/entities/${entityId}?folder=UI`;
        const appserverEndpoint = "/api/v1/promotions";
        const endpoint = conditionalEndpoint ? appserverEndpoint : priceServiceEndpoint;
        return fetchJSON<PriceEntity>(endpoint, {
            method: "PUT",
            noDomain: !conditionalEndpoint,
            body: data,
        });
    }

    addPricingCondition(base: Partial<PriceEntity>, options: PriceConditionOptions): Partial<PriceEntity> {
        const condition: PriceCondition = {
            boolean_condition: null,
            order_custom_conditions: [],
            company: null,
            customer_group: null,
            order_sum_condition: null,
            promo: null,
            company_country: null,
            price_sheet: null,
            order_bundle_conditions: null,
            delivery_custom_conditions: null,
            currency_condition: {
                currency_codes: [],
            },
            shipping_country: null,
            billing_country: null,
            bundle_condition: null,
            item_custom_conditions: null,
            sku_condition: null,
            product_condition: null,
            product_category_condition: null,
            order_total: null,
            conditional_date_range_condition: { date_from: null, date_to: null, eligible_company_ids: [] },
            shipped_amount_condition: null,
        };
        const {
            sku_ids,
            order_total,
            product_ids,
            company_ids,
            customer_group_ids,
            category_ids,
            boolean_condition,
            currencies,
            conditional_date_range_condition,
            shipped_amount_condition,
        } = options;

        condition.currency_condition.currency_codes = currencies;

        if (!isEmpty(sku_ids)) {
            condition.sku_condition = { sku_ids };
        }

        if (!isEmpty(product_ids)) {
            condition.product_condition = { product_ids };
        }

        if (!isEmpty(category_ids)) {
            condition.product_category_condition = {
                product_category_ids: category_ids,
            };
        }

        if (!isEmpty(company_ids)) {
            condition.company = { company_ids };
        }

        if (!isEmpty(customer_group_ids)) {
            condition.customer_group = { customer_group_ids };
        }

        if (!isEmpty(boolean_condition)) {
            condition.boolean_condition = boolean_condition;
        }

        if (!isEmpty(order_total)) {
            condition.order_total = order_total;
        }

        if (!isEmpty(conditional_date_range_condition)) {
            condition.conditional_date_range_condition = conditional_date_range_condition;
        }

        if (!isEmpty(shipped_amount_condition)) {
            condition.shipped_amount_condition = shipped_amount_condition;
        }
        base.price_condition = condition;
        return base;
    }

    addPricingActions(
        base: Partial<PriceEntity>,
        options: PriceActionOptions,
        selectedDiscounts: (OrderLevelDiscount | ProductLevelDiscount)[],
    ): Partial<PriceEntity> {
        const actions = base.actions || [];
        const BASE_PRICE_LEVEL = "10";

        for (const selectedDiscount of selectedDiscounts) {
            const { scope, type, actionType } = PromotionUtility.getFieldsDependsOnPromotionLevel(
                options,
                selectedDiscount,
            );
            if (selectedDiscount === OrderLevelDiscount.NET_TERMS_EXTENSION && selectedDiscounts.length > 1) {
                continue;
            }
            const priceCode =
                selectedDiscount === OrderLevelDiscount.PERCENTAGE_OFF
                    ? PriceCode.Subtotal
                    : PriceCode.AdjustedWholesale;

            const amountCondition = generateAmountCondition(options.amount, selectedDiscount, priceCode);

            let nameMessage = base.name;
            let messageVariables = {};
            const messageVariable = PromotionUtility.getMessageVariable(base, options.isThreshold, options.isQty);
            if (!isEmpty(messageVariable)) {
                const currencySymbol =
                    (messageVariable.name === MessageVariableName.AMOUNT &&
                        getCurrencyInfo(base.price_condition.currency_condition.currency_codes[0])?.symbol) ||
                    "";
                nameMessage = `${base.name} - add ${currencySymbol}%%${messageVariable.name}%% to qualify`;
                messageVariables = { message_variables: [messageVariable] };
            }

            const messages = options.messages || [nameMessage, base.description];
            if (selectedDiscounts.includes(OrderLevelDiscount.NET_TERMS_EXTENSION)) {
                messages.push(`Net ${options?.netTermsExtension || 0}`);
            }

            const action: Action = {
                price_action: {
                    price_code: actionType,
                    amount: amountCondition,
                    scope: scope,
                    price_level: BASE_PRICE_LEVEL,
                    type: type,
                    custom_localized_message: {
                        default_language: "",
                        custom_messages: [
                            {
                                language: "",
                                label: "",
                                messages,
                            },
                        ],
                        ...messageVariables,
                    },
                },
            };
            actions.push(action);
        }
        base.actions = actions;
        return base;
    }

    getBasePromotionRequest(options: BaseRequestOptions): BasePromotionRequest {
        return {
            id: options.isEditMode ? options.entityId : options.id,
            merchant_id: options.merchant_id,
            name: options.name,
            description: options.description,
            price_type: PriceType.Pricing,
            folder: UI_FOLDER,
            entity_template: "",
            enabled: options.enabled,
            pricing_start_date: options.pricingStartDate ? options.pricingStartDate.startOf("day").toDate() : null,
            pricing_end_date: options.pricingEndDate ? options.pricingEndDate.toDate() : null,
            visible_start_date: options.promotionActiveStartDate.toDate(),
            visible_end_date: options.promotionActiveEndDate && options.promotionActiveEndDate.toDate(),
            details: PromotionUtility.addPromotionDetails(options),
            created_by: options.created_by,
            created_date: null,
            last_updated_by: "",
            last_updated_date: moment().toDate(),
            revision: "1",
        };
    }

    private static addPromotionDetails(options: BaseRequestOptions): Partial<BasePromotionRequest> {
        let details = null;

        details = {
            ...details,
            ...(options.promotionStackingConfig && { promotionStackingConfig: options.promotionStackingConfig }),
            ...(options.netTermsExtension && { net_terms_extension: Number(options.netTermsExtension) || 0 }),
        };

        return details;
    }

    private static getFieldsDependsOnPromotionLevel(
        options: PriceActionOptions,
        selectedDiscount?: OrderLevelDiscount | ProductLevelDiscount,
    ): Fields {
        const { isThreshold, isLineItemLevel, priceActionId } = options;
        const isFreeShipping = selectedDiscount === OrderLevelDiscount.FREE_SHIPPING;
        const scope =
            isLineItemLevel && !isFreeShipping
                ? PromotionScope.PRICE_ACTION_SCOPE_LINE_ITEM
                : PromotionScope.PRICE_ACTION_SCOPE_TOTAL;
        const type = isThreshold
            ? PromotionType.PRICE_SECTION_TYPE_PROMOTION_THRESHOLD
            : PromotionType.PRICE_SECTION_TYPE_PROMOTION_APPLIED;

        return {
            scope,
            type,
            actionType: this.getActionType(isFreeShipping, isThreshold, priceActionId),
        };
    }

    private static getActionType(isFreeShipping: boolean, isThreshold: boolean, priceActionId: string): string {
        /**
         * In the case of THRESHOLD promotions, frontend creates 2 priceActions:
         *  - One for when the order does not meet the promotion conditions with actionType threshold|:id
         *  - One for when the order does meet the promotion condition with actionType discounts|:id
         *
         * The order switches between the 2 pricing acitons depending on wether the conditions are met.
         */

        if (isFreeShipping && isThreshold) {
            return `${PriceActionType.THRESHOLD}shipping|`;
        }

        const actionType: string = isFreeShipping
            ? PriceActionType.SHIPPING
            : isThreshold
              ? PriceActionType.THRESHOLD
              : PriceActionType.DISCOUNT;

        return `${actionType}${!(actionType === PriceActionType.SHIPPING) ? priceActionId : ""}`;
    }

    private static getMessageVariable(
        base: Partial<PriceEntity>,
        isThreshold: boolean,
        isQty: boolean,
    ): MessageVariable {
        if (!isThreshold) {
            return;
        }

        return isQty
            ? {
                  name: MessageVariableName.QTY,
                  value: {
                      subtract: {
                          first: { constant: base.price_condition.order_total.upper_bound_exclusive },
                          second: base.price_condition.order_total.amount,
                      },
                  },
                  format: MessageVariableType.NUMERIC_FORMAT_INTEGER,
              }
            : {
                  name: MessageVariableName.AMOUNT,
                  value: {
                      subtract: {
                          first: { constant: base.price_condition.order_total.upper_bound_exclusive },
                          second: base.price_condition.order_total.amount,
                      },
                  },
                  format: MessageVariableType.NUMERIC_FORMAT_MONEY,
              };
    }
}
